import { useState, useEffect, useCallback } from 'react';
import Head from 'next/head';
import { GetStaticProps } from 'next';
import Layout from 'components/layout';
import Header from 'components/banner';
import { actionServer } from 'action/index';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import { platform as getPlatform, IPlatTest } from 'utils/index';
import openApp from 'utils/openApp';
import { downloadCount } from 'utils/statics';
import style from 'styles/download.module.scss';
import DownloadOut from 'components/openOutDownload';
import { IDesc, ILinuxPlatform, IMG_PATH, IPlatform, IPlatformInfo, platforms as initPlatforms, linuxPlatform } from 'constants/download';

export default function Download() {
  const { t } = useTranslation('common');
  const [isGuide, setGuide] = useState(false);
  const [platforms, setPlatform] = useState<(IPlatformInfo | IPlatformInfo[])[]>(initPlatforms);
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [linuxPlat, setLinuxPlat] = useState<IPlatformInfo[]>(linuxPlatform);
  const [selectedOption, setSelectedOption] = useState<IPlatformInfo>(linuxPlatform[0]);
  const [plat, setPlat] = useState<IPlatTest | null>(null);
  const { locale } = router;

  const menu = [
    {
      link: 'https://cdn.xylink.com/agreement/app-authority.html',
      text: t('nav.appPermission'),
      target: 'blank',
      key: 'authority',
    },
    {
      link: 'https://cdn.xylink.com/agreement/privacy-agreement.html',
      text: t('nav.priavtePolicy'),
      target: 'blank',
      key: 'privacy',
    },
  ];

  const btnTextMap = {
    Windows: t('content.windows'),
    Mac: t('content.mac'),
    Android: t('content.android'),
    IOS: t('content.iOS'),
    Harmony_app: t('content.harmonyApp'),
    Outlook: t('content.outlook'),
    // AndroidEdu: '小鱼云课堂Android版下载',

    //信创包
    Amd64: t('content.linux.amd64'),
    Arm64: t('content.linux.arm64'),
    Mips: t('content.linux.mips'),
    Loongarch64: t('content.linux.loongarch64'),
    Amd64_kylin: t('content.linux.amd64_kylin'),
    Arm64_kylin: t('content.linux.arm64_kylin'),
    Mips_kylin: t('content.linux.mips_kylin'),
    Mips_sp1: t('content.linux.mips_sp1'),
    Loongarch64_kylin: t('content.linux.loongarch64_kylin'),
  };

  const descMap = {
    Outlook: { title: t('content.outlookTitle'), desc1: t('content.outlookDesc1'), desc2: t('content.outlookDesc2') },
    userGuide: { title: 'User Guide Download', desc1: 'XYLink Video Conferencing PC Client User Guide', desc2: 'XYLink Video Conferencing App User Guide' },
  };

  //信创包hover提示
  const linuxTipMap = {
    Amd64: t('content.linuxTip.amd64'),
    Arm64: t('content.linuxTip.arm64'),
    Mips: t('content.linuxTip.mips'),
    Loongarch64: t('content.linuxTip.loongarch64'),
    Amd64_kylin: t('content.linuxTip.amd64_kylin'),
    Arm64_kylin: t('content.linuxTip.arm64_kylin'),
    Mips_kylin: t('content.linuxTip.mips_kylin'),
    Mips_sp1: t('content.linuxTip.mips_sp1'),
    Loongarch64_kylin: t('content.linuxTip.loongarch64_kylin'),
  };

  const onWindowLoad = useCallback(() => {
    // 非微信且要打开app
    if (plat && !plat.weixin && router.isReady && router.query.source) {
      const _hmt = window._hmt || [];
      if (router.query.source === 'ios-wx') {
        _hmt.push(['_trackEvent', '狰', '微信下载页iOS']);
      } else if (router.query.source === 'android-wx') {
        _hmt.push(['_trackEvent', '政', '微信下载页Android']);
      }
      openApp();
    }
  }, [plat, router.isReady, router.query.source]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionSelect = (platform: IPlatformInfo) => {
    setSelectedOption(platform);
    setIsOpen(false);
  };

  const renderClient = (platformItem: IPlatformInfo | IPlatformInfo[]) => {
    const onDownload = (platformItem: IPlatformInfo) => {
      const { key, target = '_self', appUrl, downloadCountLabel } = platformItem;
      if (plat && plat.weixin) {
        if ((plat.android && key === 'Android') || (plat.ios && key === 'IOS')) {
          setGuide(true);
          return;
        }
      }
      window.open(appUrl, target);
      if (downloadCountLabel) {
        downloadCount(router.query, {
          category: downloadCountLabel,
        });
      }
      // if (statisticsSearchLabel) {
      //接口已经不维护了，会超时
      //   action.statisticsSearch(statisticsSearchLabel);
      // }
    };

    const renderCommonClient = (platformItem: IPlatformInfo) => {
      const { key, versionName, guideUrl, id = key } = platformItem;
      return (
        <div key={key} className={cn(style.client, style['client' + key])} id={id}>
          {platformItem.key === 'Outlook' ? (
            <div className={style.clientImage}></div>
          ) : (
            <img src={`${IMG_PATH}${platformItem.key.toLowerCase()}.png`} alt={platformItem.key} className={style.clientImage}></img>
          )}
          <span
            className={style.downloadBtn}
            onClick={() => {
              onDownload(platformItem);
            }}
          >
            {btnTextMap[key as IPlatform] || ''}
          </span>
          <div className={cn(style.clientInfo, style.flexCenter)}>
            {versionName && <span className={style.version}>{versionName}</span>}
            {guideUrl && (
              <a rel="noopener noreferrer" target="_blank" href={guideUrl} className={cn(style.guide, !versionName && style.noAfterLine)}>
                {t('content.manual')}
              </a>
            )}
          </div>
        </div>
      );
    };

    const renderLinuxClient = (platformItem: IPlatformInfo[]) => {
      return (
        <div className={cn(style.client, style.clientLinux)}>
          <img src={`${IMG_PATH}linux.png`} alt="LinuxImg" className={style.clientImage}></img>
          <div className={style.dropdownContainer}>
            <div className={cn(style.dropdown, isOpen && style.open)} onClick={toggleDropdown}>
              <div className={style.selectedOption}>
                <div className={style.text}>{btnTextMap[selectedOption.key as IPlatform] || ''}</div>
                <img className={style.arrow} src={`${IMG_PATH}arrowDown.png`} alt="" />
              </div>
              <ul className={style.options}>
                {isOpen &&
                  platformItem.map((item: IPlatformInfo, index: number) => (
                    <li key={index} onClick={() => handleOptionSelect(item)} className={cn(item.key === selectedOption.key && style.active)}>
                      {btnTextMap[item.key as IPlatform] || ''}
                      <span className={style.tip}>{linuxTipMap[item.key as ILinuxPlatform] || ''}</span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
          <span
            className={cn(style.downloadBtn, !selectedOption.versionName && !selectedOption.guideUrl && style.addBottom)}
            onClick={() => {
              onDownload(selectedOption);
            }}
          >
            {t('content.linuxDownload')}
          </span>
          <div className={cn(style.clientInfo, style.flexCenter)}>
            {selectedOption.versionName && <span className={style.version}>{selectedOption.versionName}</span>}
            {selectedOption.guideUrl && (
              <a rel="noopener noreferrer" target="_blank" href={selectedOption.guideUrl} className={cn(style.guide, !selectedOption.versionName && style.noLine)}>
                {t('content.manual')}
              </a>
            )}
          </div>
        </div>
      );
    };

    return Array.isArray(platformItem) ? renderLinuxClient(platformItem) : renderCommonClient(platformItem);
  };

  useEffect(() => {
    (async () => {
      const currPlatforms = JSON.parse(JSON.stringify(initPlatforms));
      const promises = currPlatforms.map((item: IPlatformInfo) => {
        if ('userGuide' === item.key) {
          return item;
        }
        //信创客户端的
        if (!Array.isArray(item)) return actionServer.getClientInfo(item.platform);
      });

      await Promise.all(promises).then((results: any) => {
        results.forEach((item: any, index: number) => {
          const { versionName = '', appUrl, platform = '' } = item || {};

          currPlatforms[index].versionName = versionName ? 'V' + versionName : '';
          //ios、harmonyAPP需要跳转到App Store,使用本地的appUrl
          if (platform !== 'ios_office' && platform !== 'harmony_app') {
            currPlatforms[index].appUrl = appUrl;
          }
        });
      });
      setPlatform(currPlatforms);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      //取信创客户端的信息
      const currLinuxPlats = JSON.parse(JSON.stringify(linuxPlatform));

      const promises = currLinuxPlats.map((item: IPlatformInfo) => {
        return actionServer.getClientInfo(item.platform);
      });

      await Promise.all(promises).then((results: any) => {
        results.forEach((item: any, index: number) => {
          const { versionName = '', appUrl = '' } = item || {};
          currLinuxPlats[index].versionName = versionName ? 'V' + versionName : '';
          currLinuxPlats[index].appUrl = appUrl;
        });
      });
      setLinuxPlat(currLinuxPlats);
      setSelectedOption(currLinuxPlats[0]);
    })();
  }, []);

  useEffect(() => {
    setPlat(getPlatform());
  }, []);

  useEffect(() => {
    if (plat && router.isReady) {
      if (router.asPath.indexOf('#') === -1) {
        if (plat.ios) {
          router.replace(`${router.asPath}#iosAnchor`);
        } else if (plat.android) {
          router.replace(`${router.asPath}#androidAnchor`);
        }
      }
    }
  }, [plat, router]);

  useEffect(() => {
    window.addEventListener('load', onWindowLoad);
    return () => {
      window.removeEventListener('load', onWindowLoad);
    };
  }, [onWindowLoad]);

  // 安卓微信 或者要微信打开app 需要跳出去
  if (plat?.weixin && (isGuide || router.query.source)) {
    return <DownloadOut />;
  }

  return (
    <Layout isNeedI18n={true}>
      <Head>
        <title>{t('nav.title')}</title>
      </Head>
      <section className={style.container}>
        <Header
          pageKey="download"
          menu={menu}
          title={t('nav.downloadCenterHeader')}
          bgImage="https://testdevcdn.xylink.com/h5/sdk_doc/static/website/double-classroom/mobile/55241944ba9a4030d52d4fbcb3c6a855d700edc6c9a02493fcfdf9b300913471.jpg"
        />
      </section>
      {/* windows、Mac、Linux、Android、iOS */}
      {[1, 2].map((rowIndex: number) => {
        const plats_row1 = [...platforms.slice(0, 2), linuxPlat];
        const plats_row2 = platforms.slice(3, 6);
        const platformItems = rowIndex === 1 ? plats_row1 : plats_row2;
        return (
          <div key={rowIndex} className={cn(style.clients, style.flexCenter)}>
            {platformItems.map((platformItem: IPlatformInfo | IPlatformInfo[], index: number) => {
              return <div key={index}>{renderClient(platformItem)}</div>;
            })}
          </div>
        );
      })}

      {/* Outlook插件、User Guide、小鱼云课堂 */}
      {platforms.slice(6).map((platformItem: IPlatformInfo | IPlatformInfo[], index: number) => {
        const { key, guideUrl, guideUrl2 = '', title } = platformItem as IPlatformInfo;
        //outlook插件下载只显示在windows端
        if (index === 0 && !plat?.windows) {
          return null;
          //userGuide只显示在英文环境下
        } else if (index === 1) {
          if (locale === 'en-US') {
            return (
              <div key={key} className={cn(style.clients, style.flexCenter)}>
                <div className={cn(style.client, style.clientUserGuide)}>
                  <div className={style.clientImage}></div>
                </div>
                <div className={style.introduction}>
                  <p className={style.title}>{title}</p>
                  <a className={style.link} rel="noopener noreferrer" target="_blank" href={guideUrl}>
                    {descMap[key as IDesc].desc1 || ''}
                  </a>
                  <a className={style.link} rel="noopener noreferrer" target="_blank" href={guideUrl2}>
                    {descMap[key as IDesc].desc2 || ''}
                  </a>
                </div>
              </div>
            );
          } else {
            return null;
          }
          //小鱼云课堂只显示在中文环境下（现暂时隐藏）
        } else if (index === 2 && locale === 'en-US') {
          return null;
        } else {
          return (
            <div key={key} className={cn(style.clients, style.flexCenter, key === 'AndroidEdu' && style.clientsAndroidEdu)}>
              {renderClient(platformItem)}
              <div className={style.introduction}>
                <p className={style.title}> {descMap[key as IDesc].title || ''}</p>
                <p className={style.text}>
                  {descMap[key as IDesc].desc1 || ''}
                  <br className={style.forPC} />
                  {descMap[key as IDesc].desc2 || ''}
                </p>
              </div>
            </div>
          );
        }
      })}

      <div className={style.bottom}>{t('content.developer')}</div>
    </Layout>
  );
}

export const getStaticProps: GetStaticProps = async (context: any) => {
  return {
    props: {
      ...(await serverSideTranslations(context.locale, ['common'])),
    },
  };
};
